import type { Collider, RigidBody, World } from "@dimforge/rapier3d-compat";
import * as THREE from "three";
import { readBody, type BodyState } from "./physics/body";
import type { Shape } from "./physics/materials";
import { createBlockBody, type Rapier } from "./physics/world";
import type { Material } from "./render/materials";

/* =====================================================================
   BLOCKS
   Each block is a Rapier body and a Three.js mesh, kept in step every frame.
   ===================================================================== */
export const MAX_BLOCKS = 160;

export interface Block extends BodyState {
  mesh: THREE.Mesh<THREE.BoxGeometry, THREE.MeshStandardMaterial>;
  body: RigidBody;
  collider: Collider;
  pos: THREE.Vector3;
  quat: THREE.Quaternion;
}

export type Blocks = ReturnType<typeof createBlocks>;

export function createBlocks(RAPIER: Rapier, world: World, scene: THREE.Scene) {
  /** Reads a block's body and moves its mesh to match. */
  function readInto(b: Block) {
    readBody(b.body, b);
    b.mesh.position.copy(b.pos);
    b.mesh.quaternion.copy(b.quat);
  }

  const blocks = {
    list: [] as Block[],
    placed: 0,
    fallenTotal: 0,

    spawn(mat: Material, shape: Shape, pos: THREE.Vector3, yaw: number, born: number) {
      const [w, h, d] = shape.size;
      const mesh = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat.visual);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      scene.add(mesh);

      const { body, collider } = createBlockBody(RAPIER, world, mat, shape.size, pos, yaw);

      const entry: Block = {
        mesh,
        body,
        collider,
        born,
        size: shape.size,
        pos: new THREE.Vector3(),
        quat: new THREE.Quaternion(),
        aabb: { lowerBound: { x: 0, y: 0, z: 0 }, upperBound: { x: 0, y: 0, z: 0 } },
        speed: 0,
        spin: 0,
        asleep: false,
      };
      // Placed at once: a block dropped after this frame's sync is drawn before
      // the next one, and a new mesh stands at the centre of the grid
      readInto(entry);
      mesh.userData.block = entry; // lets a raycast hit find its block
      blocks.list.push(entry);
      blocks.placed++;
      if (blocks.list.length > MAX_BLOCKS) blocks.remove(blocks.list[0]);
    },

    /** Takes a block out of both worlds and the list. */
    remove(b: Block) {
      world.removeRigidBody(b.body); // also removes its collider
      scene.remove(b.mesh);
      b.mesh.geometry.dispose();
      const i = blocks.list.indexOf(b);
      if (i >= 0) blocks.list.splice(i, 1);
    },

    clear() {
      while (blocks.list.length) blocks.remove(blocks.list[blocks.list.length - 1]);
      blocks.placed = 0;
      blocks.fallenTotal = 0;
    },

    /** Reads every body after the physics steps and moves its mesh to match. */
    sync() {
      for (const b of blocks.list) readInto(b);
      // Blocks that fall into the void are removed from both worlds
      for (let i = blocks.list.length - 1; i >= 0; i--) {
        if (blocks.list[i].pos.y < -15) {
          blocks.remove(blocks.list[i]);
          blocks.fallenTotal++;
        }
      }
    },

    // Sleeping bodies don't notice when their support disappears, so wake them all
    wakeAll() {
      for (const k of blocks.list) k.body.wakeUp();
    },
  };
  return blocks;
}
